import React from "react";
import styled from "styled-components";
import { brand_orange_deep, brand_danger, dark } from "../constants/styles";

// Shared wrapper used by text inputs, selects, and password fields on the
// auth pages. Handles: label ↔ input association, error rendering, dark-mode,
// and focus-visible ring. Individual input components (text input, select,
// password) render the control via `renderControl(id, describedBy, error)`.
//
// Props:
//   id           — required, used for label/aria-describedby association
//   label        — visible label above the control
//   required     — adds a red asterisk to the label
//   hint         — optional helper text below the input (hidden when there's an error)
//   error        — string; shown below the input, also flips the input to its error state
//   renderControl(props) — callback that returns the actual <input>/<select> node

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const LabelRow = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #374151;

  [data-theme="dark"] & {
    color: ${dark.text_primary};
  }
`;

const Required = styled.span`
  color: ${brand_danger};
  margin-left: 4px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: #6b7280;

  [data-theme="dark"] & {
    color: ${dark.text_secondary};
  }
`;

const Error = styled.div`
  font-size: 12px;
  color: ${brand_danger};
  font-weight: 500;
`;

// Apply the same control styling to <input> AND <select> to keep the form
// visually coherent. Consumers of FormField call `controlCss` to compose.
export const controlCssObj = {
  width: "100%",
  minHeight: "48px",
  padding: "12px 14px",
  border: "1px solid #d1d5db",
  borderRadius: "12px",
  fontSize: "14px",
  color: "#111827",
  background: "#ffffff",
  transition: "border-color 150ms ease, box-shadow 150ms ease",
  outline: "none",
  fontFamily: "inherit",
};

// The `$hasError` prop flips to the error state. `$trailingPad` leaves room
// on the right for an icon (used by PasswordField's mask toggle).
//
// `box-sizing: border-box` is critical — without it the 100% width adds
// padding + border on top, making the input overflow its container at
// narrower desktop widths (confirmed at 1186px and below).
export const Control = styled.input`
  box-sizing: border-box;
  width: 100%;
  min-height: 48px;
  padding: 12px ${({ $trailingPad }) => ($trailingPad ? "48px" : "14px")} 12px 14px;
  border: 1px solid ${({ $hasError }) => ($hasError ? brand_danger : "#d1d5db")};
  border-radius: 12px;
  font-size: 14px;
  color: #111827;
  background: #ffffff;
  transition: border-color 150ms ease, box-shadow 150ms ease;
  outline: none;
  font-family: inherit;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    border-color: ${({ $hasError }) => ($hasError ? brand_danger : brand_orange_deep)};
    box-shadow: 0 0 0 3px ${({ $hasError }) =>
      $hasError ? "rgba(220, 38, 38, 0.18)" : "rgba(216, 89, 7, 0.18)"};
  }

  &:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
  }

  [data-theme="dark"] & {
    background: ${dark.surface_bg};
    color: ${dark.text_primary};
    border-color: ${({ $hasError }) => ($hasError ? brand_danger : "rgba(255,255,255,0.1)")};

    &::placeholder {
      color: ${dark.text_muted};
    }
  }
`;

// A generic select styled identically to the text input.
export const ControlSelect = styled.select`
  box-sizing: border-box;
  width: 100%;
  min-height: 48px;
  padding: 12px 40px 12px 14px;
  border: 1px solid ${({ $hasError }) => ($hasError ? brand_danger : "#d1d5db")};
  border-radius: 12px;
  font-size: 14px;
  color: #111827;
  background: #ffffff;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 10 13 14 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 20px 20px;
  appearance: none;
  -webkit-appearance: none;
  outline: none;
  font-family: inherit;
  cursor: pointer;

  &:focus {
    border-color: ${({ $hasError }) => ($hasError ? brand_danger : brand_orange_deep)};
    box-shadow: 0 0 0 3px ${({ $hasError }) =>
      $hasError ? "rgba(220, 38, 38, 0.18)" : "rgba(216, 89, 7, 0.18)"};
  }

  [data-theme="dark"] & {
    background-color: ${dark.surface_bg};
    color: ${dark.text_primary};
    border-color: ${({ $hasError }) => ($hasError ? brand_danger : "rgba(255,255,255,0.1)")};
  }
`;

export default function FormField({
  id,
  label,
  required,
  hint,
  error,
  children,
}) {
  const errorId = error ? `${id}-error` : undefined;
  const hintId = hint && !error ? `${id}-hint` : undefined;

  // Clone the child control to inject id + aria-describedby + error state.
  // Callers pass a single <Control /> (or similar) as the sole child.
  const described = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  const child = React.Children.only(children);
  const injected = React.cloneElement(child, {
    id,
    "aria-invalid": error ? "true" : undefined,
    "aria-describedby": described,
    $hasError: !!error,
  });

  return (
    <Wrap>
      <LabelRow htmlFor={id}>
        {label}
        {required && <Required aria-hidden="true">*</Required>}
      </LabelRow>
      {injected}
      {hint && !error && <Hint id={hintId}>{hint}</Hint>}
      {error && <Error id={errorId} role="alert">{error}</Error>}
    </Wrap>
  );
}
